import { Link } from "react-router-dom";
import { Briefcase, Ship, Mail, MessageSquareWarning } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";

/**
 * Landing page shown to service-provider accounts that haven't been added
 * to any yacht yet. Once a yacht owner / captain adds them to a crew, the
 * standard yacht navigation unlocks automatically.
 */
export default function ProviderHome() {
  const { user } = useAuth();

  return (
    <div className="p-6 lg:p-10 max-w-3xl" data-testid="provider-home">
      <div className="flex items-start gap-4 mb-8">
        <div className="w-12 h-12 rounded-md bg-ink text-white grid place-items-center shrink-0">
          <Briefcase className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
            Service provider account
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4" data-testid="provider-home-card">
        <div className="flex items-start gap-3">
          <Ship className="h-5 w-5 text-zinc-400 mt-0.5" />
          <div>
            <div className="font-medium">No yacht assignments yet</div>
            <p className="text-sm text-zinc-600 mt-1">
              You're all set up. As soon as a yacht owner or captain adds
              you to their crew on Seavanto, you'll start seeing that
              yacht's tasks, inventory and chat right here. Until then,
              there's nothing on your plate.
            </p>
          </div>
        </div>

        <div className="border-t border-zinc-100 pt-4 flex items-start gap-3">
          <Mail className="h-5 w-5 text-zinc-400 mt-0.5" />
          <div>
            <div className="font-medium">Heard from a yacht owner already?</div>
            <p className="text-sm text-zinc-600 mt-1">
              Share <span className="font-mono">{user?.email}</span> with them
              so they can add you directly — no extra step from your side.
            </p>
          </div>
        </div>

        <div className="border-t border-zinc-100 pt-4 flex items-start gap-3">
          <MessageSquareWarning className="h-5 w-5 text-zinc-400 mt-0.5" />
          <div>
            <div className="font-medium">Profile up-to-date?</div>
            <p className="text-sm text-zinc-600 mt-1">
              Make sure your phone, company and email are correct — yacht
              owners will see exactly what you set here.
            </p>
            <Button asChild variant="outline" size="sm" className="mt-2" data-testid="provider-home-settings">
              <Link to="/app/profile">Open my profile</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
